import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { CreateOperatorModal } from './Operators';
import { SuggestionCards } from '../components/operator/SuggestionCards';
import { PersonaSettings, PersonaRename } from '../components/operator/PersonaRename';
import { ConnectorUpdate } from '../components/operator/ConnectorUpdate';
import { TasksTab } from '../components/operator/TasksTab';
import { WorkingBanner } from '../components/operator/WorkingBanner';
import { WorkspaceSettings } from '../components/operator/WorkspaceSettings';
import OperatorChatModal from '../components/OperatorChatModal';
import OperatorIntelligence from '../components/OperatorIntelligence';

import {
  ArrowLeft,
  ArrowSquareOut,
  ArrowsClockwise,
  Brain,
  Calendar,
  ChatText,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  Copy,
  Cpu,
  CreditCard,
  CurrencyDollar,
  CursorClick,
  DeviceMobile,
  DownloadSimple,
  FileCode,
  GearSix,
  Globe,
  HardDrives,
  Lightning,
  Link,
  ListChecks,
  Lock,
  Monitor,
  PaperPlaneRight,
  Pause,
  PencilSimple,
  Play,
  Shield,
  ShieldSlash,
  Sparkle,
  Square,
  Terminal,
  Trash,
  User,
  WarningCircle,
  WifiHigh,
  WifiSlash
} from '@phosphor-icons/react';
// ════════════════════════════════════════════════════════════════════════════
// CAPABILITY CONFIGURATION
// ════════════════════════════════════════════════════════════════════════════

const CAPABILITY_CONFIG = {
  file_operations: { 
    label: 'File Operations', 
    icon: FileCode, 
    color: 'blue',
    description: 'Read, write, and manage files on the connected device'
  },
  browser_automation: { 
    label: 'Browser Automation', 
    icon: CursorClick, 
    color: 'purple',
    description: 'Navigate and interact with web pages using Playwright'
  },
  shell_commands: { 
    label: 'Shell Commands', 
    icon: Terminal, 
    color: 'green',
    description: 'Execute terminal commands on the connected device'
  },
  api_calls: { 
    label: 'API Calls', 
    icon: Globe, 
    color: 'orange',
    description: 'Make HTTP requests to external APIs'
  },
};

const PERSONALITY_CONFIG = {
  professional: { label: 'Professional', description: 'Formal and business-focused' },
  friendly: { label: 'Friendly', description: 'Warm and approachable' },
  concise: { label: 'Concise', description: 'Brief and to the point' },
  detailed: { label: 'Detailed', description: 'Thorough explanations' },
};

const OperatorDetail = () => {
  const { operatorId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeOrganization, loading: orgLoading } = useOrganization();
  
  const [operator, setOperator] = useState(null);
  const [connector, setConnector] = useState(null);
  const [allConnectors, setAllConnectors] = useState([]);
  const [hostingActive, setHostingActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Connector tab state (legacy WebSocket - kept for backwards compatibility)
  const [copiedBash, setCopiedBash] = useState(false);
  const [copiedPowershell, setCopiedPowershell] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [generatingPairingCode, setGeneratingPairingCode] = useState(false);
  
  // NEW: Simple API Key connector state
  const [connectorKeys, setConnectorKeys] = useState([]);
  const [connectorStatus, setConnectorStatus] = useState(null); // Real-time status
  const [newKeyName, setNewKeyName] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [newlyGeneratedKey, setNewlyGeneratedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [connectorPlatform, setConnectorPlatform] = useState('windows'); // 'windows' or 'mac'
  const [latestConnectorVersion, setLatestConnectorVersion] = useState('3.0.0'); // Dynamic version
  
  // GearSix tab state
  const [telegramStatus, setTelegramStatus] = useState(null);
  const [telegramBotUsername, setTelegramBotUsername] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  
  // Persona state (Orion feature)
  const [personaName, setPersonaName] = useState('Orion');
  
  // Chat modal state
  const [showChatModal, setShowChatModal] = useState(false);

  // Handle payment success URL parameters
  useEffect(() => {
    const hostingStatus = searchParams.get('hosting');
    
    if (hostingStatus === 'success') {
      toast.success('Operator activation successful!');
      setSearchParams({});
      // ArrowsClockwise data to get updated hosting status
      if (activeOrganization) {
        fetchOperator();
      }
    } else if (hostingStatus === 'cancelled') {
      toast.info('Activation was cancelled');
      setSearchParams({});
    }
  }, [searchParams, activeOrganization]);

  useEffect(() => {
    // Only fetch when we have both operatorId and activeOrganization
    if (operatorId && activeOrganization?.organization_id) {
      fetchOperator();
    }
  }, [operatorId, activeOrganization?.organization_id]);

  const fetchOperator = useCallback(async () => {
    // Double check we have required data
    if (!activeOrganization?.organization_id || !operatorId) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Fetch operator details
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}`,
        {},
        activeOrganization.organization_id
      );

      if (response.ok) {
        const data = await response.json();
        const agentData = data.agent || data;
        setOperator(agentData);
        
        // Update persona name from agent data
        if (agentData.persona_name) {
          setPersonaName(agentData.persona_name);
        }
        
        // Check hosting status from the response
        // API returns hosting_status: "active" or "inactive" for single agent
        // OR hosting_active: true/false for wrapped responses
        const isHostingActive = data.hosting_active || 
                                data.hosting_status === 'active' || 
                                (data.agent && data.agent.hosting_status === 'active');
        setHostingActive(isHostingActive);
        
        // If operator has a connector_id, fetch connector details
        if (data.agent?.connector_id || data.connector_id) {
          fetchConnector(data.agent?.connector_id || data.connector_id);
        }
        
        // Also fetch API keys for the overview
        fetchConnectorKeys();
      } else {
        toast.error('Operator not found');
        navigate('/partner-pro/ai-workforce/agents');
      }
    } catch (error) {
      console.error('Error fetching operator:', error);
      toast.error('Failed to load operator');
      navigate('/partner-pro/ai-workforce/agents');
    } finally {
      setLoading(false);
    }
  }, [operatorId, activeOrganization, navigate]);

  const fetchConnector = async (connectorId) => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/${connectorId}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setConnector(data.connector || data);
      } else if (response.status === 404) {
        // Connector doesn't exist anymore, clear the reference
        setConnector(null);
        // Also update the operator to remove the connector_id reference
        await fetchWithOrg(
          `${BACKEND_URL}/api/exec/agents/${operatorId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ connector_id: null })
          },
          activeOrganization.organization_id
        );
      } else {
        setConnector(null);
      }
    } catch (error) {
      console.error('Error fetching connector:', error);
      setConnector(null);
    }
  };

  // Fetch all connectors for this organization
  const fetchConnectors = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllConnectors(data.connectors || []);
      }
    } catch (error) {
      console.error('Error fetching connectors:', error);
    }
  };

  // Fetch chat integration settings
  const fetchChatSettings = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/settings`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setTelegramStatus(data.telegram);
        setWhatsappStatus(data.whatsapp);
      }
    } catch (error) {
      console.error('Error fetching chat settings:', error);
    }
  };

  // Fetch operator-specific telegram status (for pairing link)
  const fetchOperatorTelegramStatus = async () => {
    if (!operatorId) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/chat/telegram/status/${operatorId}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.configured && data.bot_username) {
          setTelegramBotUsername(data.bot_username);
        }
      }
    } catch (error) {
      console.error('Error fetching telegram status:', error);
    }
  };

  // Generate pairing code for new connector
  const handleGeneratePairingCode = async () => {
    setGeneratingPairingCode(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/pair`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent_id: operatorId })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setPairingCode(data.pairing_code);
        toast.success('Pairing code generated! Use this code when installing the connector.');
        // ArrowsClockwise connectors list to show the new connector
        fetchConnectors();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || 'Failed to generate pairing code');
      }
    } catch (error) {
      toast.error('Failed to generate pairing code');
    } finally {
      setGeneratingPairingCode(false);
    }
  };

  // Revoke connector access
  const handleRevokeConnector = async (connectorId) => {
    if (!confirm('Are you sure you want to revoke this connector? The device will be disconnected and need to be re-paired.')) {
      return;
    }
    
    setActionLoading('revoke');
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/${connectorId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success('Connector access revoked');
        setConnector(null);
        fetchConnectors();
      } else if (response.status === 404) {
        // Connector doesn't exist - clear it anyway
        toast.info('Connector already removed');
        setConnector(null);
        fetchConnectors();
      } else {
        toast.error('Failed to revoke connector');
      }
    } catch (error) {
      toast.error('Failed to revoke connector');
    } finally {
      setActionLoading(null);
    }
  };

  // Generate install command URLs
  const getInstallApiBase = () => {
    if (window.location.hostname === 'localhost') {
      return 'http://localhost:8001';
    }
    return `${window.location.protocol}//${window.location.host}`;
  };

  const bashCommand = `curl -fsSL ${getInstallApiBase()}/api/exec/connector/install.sh | bash`;
  const powershellCommand = `irm ${getInstallApiBase()}/api/exec/connector/install.ps1 | iex`;

  // Copy to clipboard handlers
  const handleCopyBash = () => {
    navigator.clipboard.writeText(bashCommand);
    setCopiedBash(true);
    toast.success('Command copied to clipboard');
    setTimeout(() => setCopiedBash(false), 2000);
  };

  const handleCopyPowershell = () => {
    navigator.clipboard.writeText(powershellCommand);
    setCopiedPowershell(true);
    toast.success('Command copied to clipboard');
    setTimeout(() => setCopiedPowershell(false), 2000);
  };

  // NEW: Fetch connector keys for this agent
  const fetchConnectorKeys = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/keys?agent_id=${operatorId}`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setConnectorKeys(data.keys || []);
      }
    } catch (error) {
      console.error('Error fetching connector keys:', error);
    }
  };

  // NEW: Generate a connector API key
  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/key/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agent_id: operatorId,
            name: newKeyName || `Connector for ${operator?.name}`
          })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setNewlyGeneratedKey(data.api_key);
        setNewKeyName('');
        toast.success('API Key generated! Save it now - you won\'t see it again.');
        fetchConnectorKeys();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to generate key');
      }
    } catch (error) {
      toast.error('Failed to generate API key');
    } finally {
      setGeneratingKey(false);
    }
  };

  // NEW: Revoke a connector key
  const handleRevokeKey = async (keyId, keyName) => {
    if (!window.confirm(`Revoke key "${keyName}"? The connector will stop working immediately.`)) return;
    
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/key/${keyId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success('Key revoked');
        fetchConnectorKeys();
      } else {
        toast.error('Failed to revoke key');
      }
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  // NEW: Copy command to clipboard
  const handleCopyCommand = (key) => {
    const command = `python connector_v3.py --key ${key}`;
    navigator.clipboard.writeText(command);
    setCopiedCommand(true);
    toast.success('Command copied!');
    setTimeout(() => setCopiedCommand(false), 2000);
  };

  // NEW: Copy key to clipboard
  const handleCopyKey = () => {
    if (newlyGeneratedKey) {
      navigator.clipboard.writeText(newlyGeneratedKey);
      setCopiedKey(true);
      toast.success('API key copied!');
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  // Load additional data when tab changes
  useEffect(() => {
    if (activeOrganization && activeTab === 'connector') {
      fetchConnectors();
      fetchConnectorKeys(); // Also fetch the new API keys
      fetchConnectorStatus(); // Fetch real-time status
    } else if (activeOrganization && activeTab === 'settings') {
      fetchChatSettings();
    } else if (activeOrganization && activeTab === 'chat') {
      fetchOperatorTelegramStatus();
    }
  }, [activeTab, activeOrganization, operatorId]);

  // Fetch real-time connector status
  const fetchConnectorStatus = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/connector/status/${operatorId}`,
        {},
        activeOrganization.organization_id
      );
      if (response.ok) {
        const data = await response.json();
        setConnectorStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch connector status:', error);
    }
  };

  // Fetch latest connector version for installation instructions
  const fetchConnectorVersion = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exec/connector/version`);
      if (response.ok) {
        const data = await response.json();
        setLatestConnectorVersion(data.version || '3.0.0');
      }
    } catch (error) {
      console.error('Failed to fetch connector version:', error);
    }
  };

  // Fetch connector version on mount
  useEffect(() => {
    fetchConnectorVersion();
  }, []);

  // Auto-refresh connector status every 30 seconds when on connector tab
  useEffect(() => {
    if (activeTab === 'connector' && activeOrganization) {
      const interval = setInterval(() => {
        fetchConnectorStatus();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, activeOrganization, operatorId]);

  // Lifecycle handlers
  const handleStart = async () => {
    setActionLoading('start');
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}/start`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success(`Operator "${operator.name}" is now running`);
        fetchOperator();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to start operator');
      }
    } catch (error) {
      toast.error('Failed to start operator');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async () => {
    setActionLoading('pause');
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}/pause`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success(`Operator "${operator.name}" has been paused`);
        fetchOperator();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to pause operator');
      }
    } catch (error) {
      toast.error('Failed to pause operator');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Are you sure you want to stop this operator? All pending commands will be cancelled.')) {
      return;
    }
    
    setActionLoading('stop');
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}/stop`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast.success(`Operator "${operator.name}" has been stopped`);
        fetchOperator();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to stop operator');
      }
    } catch (error) {
      toast.error('Failed to stop operator');
    } finally {
      setActionLoading(null);
    }
  };

  // Sync activation status from Stripe
  const handleSyncHosting = async () => {
    setActionLoading('sync');
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/hosting/sync`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success('Activation synced successfully!');
        setHostingActive(true);
        fetchOperator(); // ArrowsClockwise operator data
      } else {
        toast.error(data.message || 'No active subscription found. Please complete payment first.');
      }
    } catch (error) {
      toast.error('Failed to sync activation status');
    } finally {
      setActionLoading(null);
    }
  };

  // Trash operator and disconnect - removes connector access
  const handleDeleteAndDisconnect = async () => {
    const hasSubscription = operator.stripe_subscription_id || operator.billing_enabled || hostingActive;
    const activeKeys = connectorKeys.filter(k => k.active);
    
    let confirmMessage = `⚠️ DELETE & DISCONNECT\n\n` +
      `This will permanently:\n`;
    
    if (activeKeys.length > 0) {
      confirmMessage += `• Revoke ${activeKeys.length} API key(s) - connector will stop immediately\n`;
    }
    confirmMessage += `• Stop any running sessions\n`;
    
    if (hasSubscription) {
      confirmMessage += `• Deactivate this agent\n\n` +
        `You can reactivate from Billing within the current billing period.\n\n`;
    } else {
      confirmMessage += `\nThis action cannot be undone.\n\n`;
    }
    
    confirmMessage += `Are you sure you want to delete "${operator.name}"?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setActionLoading('delete');
    try {
      // Revoke all API keys for this agent
      for (const key of activeKeys) {
        try {
          await fetchWithOrg(
            `${BACKEND_URL}/api/exec/connector/key/${key.id}`,
            { method: 'DELETE' },
            activeOrganization.organization_id
          );
        } catch (e) {
          console.warn(`Failed to revoke key ${key.id}, continuing...`);
        }
      }
      
      // Also disconnect legacy connector if exists
      if (connector && connector.status === 'connected') {
        try {
          await fetchWithOrg(
            `${BACKEND_URL}/api/exec/connector/${connector.id}/revoke`,
            { method: 'POST' },
            activeOrganization.organization_id
          );
        } catch (e) {
          console.warn('Legacy connector revoke failed, continuing with deletion');
        }
      }
      
      // Trash the operator (backend handles subscription cancellation and soft-delete)
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/agents/${operatorId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.soft_delete && result.can_reactivate) {
          toast.success(
            `Operator "${operator.name}" deleted. ${activeKeys.length > 0 ? `${activeKeys.length} API key(s) revoked. ` : ''}Subscription cancelled. You can reactivate from Billing.`,
            { duration: 5000 }
          );
        } else {
          toast.success(
            `Operator "${operator.name}" has been permanently deleted${activeKeys.length > 0 ? ` and ${activeKeys.length} API key(s) revoked` : ''}.`,
            { duration: 5000 }
          );
        }
        
        // Navigate back to agents list
        navigate('/agents');
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to delete operator');
      }
    } catch (error) {
      toast.error('Failed to delete operator');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle activation checkout
  const handleEnableHosting = async () => {
    try {
      setCheckoutLoading(true);
      
      // Add timestamp to URLs to prevent browser caching issues on return from Stripe
      const timestamp = Date.now();
      
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/exec/hosting/subscribe`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            success_url: `${window.location.origin}/partner-pro/ai-workforce/operators/${operatorId}?hosting=success&t=${timestamp}`,
            cancel_url: `${window.location.origin}/partner-pro/ai-workforce/operators/${operatorId}?hosting=cancelled&t=${timestamp}`
          })
        },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          toast.info('Redirecting to payment...');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || error.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Failed to initiate checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      draft: { color: 'bg-zinc-500/20 text-[#B0B8C4] border-zinc-500/30', label: 'Draft', icon: Clock },
      configured: { color: 'bg-blue-500/20 text-[#3B82F6] border-blue-500/30', label: 'Configured', icon: GearSix },
      active: { color: 'bg-[#00E5C0]/20 text-[#00E5C0] border-green-500/30', label: 'Active', icon: Play },
      paused: { color: 'bg-[#F59E0B]/20 text-[#F59E0B] border-yellow-500/30', label: 'Paused', icon: Pause },
      stopped: { color: 'bg-[#FF6B6B]/20 text-[#FF6B6B] border-red-500/30', label: 'Stopped', icon: Square },
      error: { color: 'bg-[#FF6B6B]/20 text-[#FF6B6B] border-red-500/30', label: 'Error', icon: WarningCircle },
    };
    return configs[status] || configs.draft;
  };

  // Show loading while org context or operator data is being fetched
  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CircleNotch size={48} className="animate-spin text-pink-500" />
      </div>
    );
  }

  // Wait for organization to be set before showing "not found"
  if (!activeOrganization) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <CircleNotch size={48} className="animate-spin text-pink-500" />
      </div>
    );
  }

  if (!operator) {
    return (
      <div className="container mx-auto px-6 py-20">
        <p className="text-white text-center">Operator not found</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(operator.status);
  const StatusIcon = statusConfig.icon;
  const canStart = ['draft', 'configured', 'stopped', 'paused'].includes(operator.status);
  const canPause = operator.status === 'active';
  const canStop = ['active', 'paused'].includes(operator.status);

  return (
    <>
      <div className="container mx-auto px-6 py-8" data-testid="operator-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/partner-pro/ai-workforce/agents')}
          className="flex items-center gap-2 text-[#A0AEC0] hover:text-white mb-6 transition-colors"
          data-testid="back-to-agents-btn"
        >
          <ArrowLeft size={20} />
          Back to Agents
        </button>

        {/* Operator Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-8 mb-6"
          data-testid="operator-header"
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-6">
            {/* Left: Operator Info */}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                <img src="/orion-icon-192.png" alt="Orion" className="w-full h-full object-cover" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl lg:text-3xl font-bold text-white truncate" data-testid="operator-name">
                    {operator.name}
                  </h1>
                  {/* Persona Name with Rename */}
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00E5C0]/20 border border-purple-500/30">
                    <Sparkle size={12} className="text-[#00E5C0]" />
                    <PersonaRename 
                      agentId={operatorId}
                      currentName={personaName}
                      onNameChange={(newName) => {
                        setPersonaName(newName);
                        if (operator) {
                          setOperator({...operator, persona_name: newName});
                        }
                      }}
                    />
                  </div>
                </div>
                <p className="text-[#A0AEC0] text-sm mb-3 line-clamp-2" data-testid="operator-description">
                  {operator.description || 'An AI operator that executes tasks on your local machine through a secure connector.'}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {/* Operator Badge */}
                  <span className="px-2.5 py-1 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] text-xs font-semibold flex items-center gap-1" data-testid="operator-badge">
                    <img src="/orion-icon-32.png" alt="" className="w-3 h-3 rounded-sm" />
                    Orion - Operator Agent
                  </span>
                  
                  {/* Status Badge */}
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 border ${statusConfig.color}`} data-testid="status-badge">
                    <StatusIcon size={12} />
                    {statusConfig.label}
                  </span>
                  
                  {/* Memory Badge */}
                  {operator.memory_enabled && (
                    <span className="px-2.5 py-1 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] text-xs font-semibold flex items-center gap-1" data-testid="memory-badge">
                      <Brain size={12} />
                      Memory Enabled
                    </span>
                  )}
                  
                  {/* Activation Status */}
                  {!hostingActive && (
                    <span className="px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-semibold flex items-center gap-1">
                      <WarningCircle size={12} />
                      Activation Required
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Action Buttons - Compact */}
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              {/* Start Button */}
              {canStart && hostingActive && (
                <button
                  onClick={handleStart}
                  disabled={actionLoading === 'start'}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:opacity-90 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="start-operator-btn"
                >
                  {actionLoading === 'start' ? (
                    <CircleNotch size={14} className="animate-spin" />
                  ) : (
                    <Play size={14} />
                  )}
                  Start
                </button>
              )}
              
              {/* Pause Button */}
              {canPause && (
                <button
                  onClick={handlePause}
                  disabled={actionLoading === 'pause'}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-yellow-500 to-amber-500 hover:opacity-90 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="pause-operator-btn"
                >
                  {actionLoading === 'pause' ? (
                    <CircleNotch size={14} className="animate-spin" />
                  ) : (
                    <Pause size={14} />
                  )}
                  Pause
                </button>
              )}
              
              {/* Stop Button */}
              {canStop && (
                <button
                  onClick={handleStop}
                  disabled={actionLoading === 'stop'}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-500 to-rose-500 hover:opacity-90 text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="stop-operator-btn"
                >
                  {actionLoading === 'stop' ? (
                    <CircleNotch size={14} className="animate-spin" />
                  ) : (
                    <Square size={14} />
                  )}
                  Stop
                </button>
              )}
              
              {/* Chat with Orion Button */}
              <button
                onClick={() => setShowChatModal(true)}
                disabled={!hostingActive}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                  hostingActive
                    ? 'bg-gradient-to-r from-[#00E5C0] to-[#00CCB0] hover:opacity-90 text-white'
                    : 'bg-[#1E2836] text-[#7D8896] cursor-not-allowed'
                }`}
                data-testid="chat-with-orion-btn"
                title={!hostingActive ? 'Activate operator to start chatting' : 'Chat with Orion'}
              >
                <ChatText size={14} />
                Chat
              </button>

              <button
                onClick={() => setWizardOpen(true)}
                data-testid="edit-operator-btn"
                className="px-3 py-1.5 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white text-sm font-medium flex items-center gap-1.5"
              >
                <PencilSimple size={14} />
                PencilSimple
              </button>

              {/* Trash & Disconnect Button - Always visible for security reassurance */}
              <button
                onClick={handleDeleteAndDisconnect}
                disabled={actionLoading === 'delete'}
                className="px-3 py-1.5 rounded-lg border border-red-500/30 bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 transition-colors"
                data-testid="delete-operator-btn"
              >
                {actionLoading === 'delete' ? (
                  <CircleNotch size={14} className="animate-spin" />
                ) : (
                  <ShieldSlash size={14} />
                )}
                Trash
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-4">
            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
                <Brain size={16} />
                Personality
              </div>
              <p className="text-white font-semibold capitalize">
                {PERSONALITY_CONFIG[operator.personality]?.label || operator.personality || 'Professional'}
              </p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
                <Lightning size={16} />
                Capabilities
              </div>
              <p className="text-white font-semibold">
                {operator.capabilities?.length || 0} enabled
              </p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
                <DeviceMobile size={16} />
                Connector
              </div>
              <p className={`font-semibold ${
                connectorStatus?.is_polling ? 'text-[#00E5C0]' : 
                connectorStatus?.status === 'recent' ? 'text-[#F59E0B]' : 
                connector ? 'text-[#FF6B6B]' : 'text-[#B0B8C4]'
              }`}>
                {connectorStatus?.is_polling ? 'Online' : 
                 connectorStatus?.status === 'recent' ? 'Idle' :
                 connector ? 'Offline' : 'Not Connected'}
              </p>
            </div>

            <div className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-2">
                <Calendar size={16} />
                Created
              </div>
              <p className="text-white font-semibold">
                {new Date(operator.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'overview', label: 'Overview', icon: Cpu },
            { id: 'tasks', label: 'Tasks', icon: ListChecks },
            { id: 'intelligence', label: 'Intelligence', icon: Brain },
            { id: 'connector', label: 'Connector', icon: WifiHigh },
            { id: 'chat', label: 'Chat', icon: ChatText },
            { id: 'settings', label: 'Settings', icon: GearSix },
          ].map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#00E5C0]/20 text-[#00E5C0] border border-[#00E5C0]/30'
                    : 'glass text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Proactive Suggestions - Orion Feature */}
              <SuggestionCards 
                agentId={operatorId} 
                personaName={personaName}
                onSuggestionAction={(action, id) => {
                  console.log('[SUGGESTION]', action, id);
                }}
              />
              
              {/* Working Banner - Shows when agent is processing */}
              <WorkingBanner 
                operatorId={operatorId} 
                onViewTasks={() => setActiveTab('tasks')}
              />
              
              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* What is an Operator Agent */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Cpu size={24} className="text-pink-500" />
                What this agent does
              </h2>
              
              <p className="text-[#A0AEC0] mb-4">
                <strong className="text-white">Operator Agents</strong> are AI-powered assistants that can execute tasks directly on your local machine through a secure connector. Unlike standard agents that process data in the cloud, Operators can:
              </p>
              
              <ul className="space-y-2 text-[#A0AEC0]">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#00E5C0] flex-shrink-0 mt-1" />
                  <span>Access and manage files on your computer</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#00E5C0] flex-shrink-0 mt-1" />
                  <span>Automate browser interactions with Playwright</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#00E5C0] flex-shrink-0 mt-1" />
                  <span>Execute shell commands and scripts</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-[#00E5C0] flex-shrink-0 mt-1" />
                  <span>Interact via Telegram or WhatsApp for approvals</span>
                </li>
              </ul>
            </motion.div>
            
            {/* Configuration */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <GearSix size={24} className="text-pink-500" />
                Configuration
              </h2>

              <div className="space-y-4">
                {/* Description */}
                {operator.description && (
                  <div>
                    <label className="text-[#A0AEC0] text-sm mb-1 block">Description</label>
                    <p className="text-white">{operator.description}</p>
                  </div>
                )}
                
                {/* Personality */}
                <div>
                  <label className="text-[#A0AEC0] text-sm mb-1 block">Personality Style</label>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] text-sm font-semibold">
                      {PERSONALITY_CONFIG[operator.personality]?.label || 'Professional'}
                    </span>
                    <span className="text-[#A0AEC0] text-sm">
                      {PERSONALITY_CONFIG[operator.personality]?.description || ''}
                    </span>
                  </div>
                </div>

                {/* Capabilities */}
                {operator.capabilities && operator.capabilities.length > 0 && (
                  <div>
                    <label className="text-[#A0AEC0] text-sm mb-2 block">Enabled Capabilities</label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {operator.capabilities.map((cap, idx) => {
                        const capConfig = CAPABILITY_CONFIG[cap];
                        const CapIcon = capConfig?.icon || Lightning;
                        return (
                          <div
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]"
                          >
                            <div className="w-8 h-8 rounded-lg bg-[#00E5C0]/10 flex items-center justify-center flex-shrink-0">
                              <CapIcon size={16} className="text-[#00E5C0]" />
                            </div>
                            <div>
                              <p className="text-white text-sm font-medium">{capConfig?.label || cap}</p>
                              <p className="text-[#A0AEC0] text-xs">{capConfig?.description || ''}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* System Prompt */}
                {operator.system_prompt && (
                  <div>
                    <label className="text-[#A0AEC0] text-sm mb-1 block">System Instructions</label>
                    <div className="p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                      <p className="text-white text-sm whitespace-pre-wrap">{operator.system_prompt}</p>
                    </div>
                  </div>
                )}

                {/* Safety GearSix */}
                <div>
                  <label className="text-[#A0AEC0] text-sm mb-2 block">Safety Settings</label>
                  <div className="flex flex-wrap gap-2">
                    {operator.require_approval_for_destructive && (
                      <span className="px-3 py-1 rounded-full bg-amber-500/10 text-amber-400 text-sm flex items-center gap-1">
                        <Shield size={12} />
                        Requires Approval for Destructive Actions
                      </span>
                    )}
                    {operator.auto_approve_read_only && (
                      <span className="px-3 py-1 rounded-full bg-[#00E5C0]/10 text-[#00E5C0] text-sm flex items-center gap-1">
                        <CheckCircle size={12} />
                        Auto-approve Read-Only Actions
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* API Connection Status */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="glass rounded-2xl p-6"
            >
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Lock size={24} className="text-pink-500" />
                API Connection
              </h2>

              {/* Show payment required message if not active */}
              {!hostingActive && (
                <div className="mb-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <CreditCard size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-amber-400 font-semibold mb-1">Activation Required</h4>
                      <p className="text-amber-200/80 text-sm mb-3">
                        Activate this Operator to connect devices and run tasks. Free hosting — compute via credits.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleEnableHosting}
                          disabled={checkoutLoading}
                          className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors text-sm font-medium inline-flex items-center gap-2"
                        >
                          {checkoutLoading ? (
                            <CircleNotch size={16} className="animate-spin" />
                          ) : (
                            <CreditCard size={16} />
                          )}
                          Activate Operator
                        </button>
                        <button
                          onClick={handleSyncHosting}
                          disabled={actionLoading === 'sync'}
                          className="px-4 py-2 rounded-lg bg-[#1A2030] text-[#F0F4F8] hover:bg-zinc-600 transition-colors text-sm font-medium inline-flex items-center gap-2"
                          title="Already paid? Click to sync your status"
                        >
                          {actionLoading === 'sync' ? (
                            <CircleNotch size={16} className="animate-spin" />
                          ) : (
                            <ArrowsClockwise size={16} />
                          )}
                          Already Paid? Sync
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Show active API keys */}
              {connectorKeys.filter(k => k.active).length > 0 ? (
                <div className="space-y-3">
                  {connectorKeys.filter(k => k.active).map(key => (
                    <div 
                      key={key.id}
                      className="flex items-start gap-4 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]"
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        key.last_used_at ? 'bg-[#00E5C0]/10' : 'bg-[#1A2030]'
                      }`}>
                        {key.last_used_at ? (
                          <WifiHigh size={24} className="text-[#00E5C0]" />
                        ) : (
                          <WifiSlash size={24} className="text-[#B0B8C4]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold">{key.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            key.last_used_at 
                              ? 'bg-[#00E5C0]/20 text-[#00E5C0]' 
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {key.last_used_at ? 'Connected' : 'Waiting for Connection'}
                          </span>
                        </div>
                        <p className="text-[#A0AEC0] text-sm">
                          <span className="font-mono text-[#7D8896]">{key.key_prefix}...</span>
                          {key.device_name && ` • ${key.device_name}`}
                          {key.device_os && ` • ${key.device_os}`}
                        </p>
                        {key.last_used_at && (
                          <p className="text-[#7D8896] text-xs mt-1">
                            Last active: {new Date(key.last_used_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Show hint if keys exist but none connected yet */}
                  {connectorKeys.filter(k => k.active && !k.last_used_at).length > 0 && hostingActive && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-[#3B82F6] text-sm flex items-center gap-2">
                        <WarningCircle size={16} />
                        API key generated. Run the connector on your machine to complete the connection.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[#1E2836] flex items-center justify-center mx-auto mb-4">
                    <Lock size={32} className="text-[#7D8896]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">No API Keys</h3>
                  <p className="text-[#A0AEC0] text-sm mb-4">
                    {hostingActive 
                      ? 'Generate an API key to connect your device to this operator.'
                      : 'Activate this operator first, then generate an API key to connect.'}
                  </p>
                  {hostingActive && (
                    <button
                      onClick={() => setActiveTab('connector')}
                      data-testid="setup-connector-btn"
                      className="px-4 py-2 rounded-lg bg-[#00E5C0]/10 text-[#00E5C0] hover:bg-[#00E5C0]/20 transition-colors"
                    >
                      Generate API Key
                    </button>
                  )}
                </div>
              )}
            </motion.div>

            {/* Chat Integration */}
            {(operator.chat_provider && operator.chat_provider !== 'none') && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <ChatText size={24} className="text-pink-500" />
                  Chat Integration
                </h2>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    operator.chat_provider === 'telegram' ? 'bg-blue-500/10' : 'bg-[#00E5C0]/10'
                  }`}>
                    {operator.chat_provider === 'telegram' ? (
                      <PaperPlaneRight size={24} className="text-[#3B82F6]" />
                    ) : (
                      <ChatText size={24} className="text-[#00E5C0]" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold capitalize">{operator.chat_provider}</h3>
                    <p className="text-[#A0AEC0] text-sm">
                      Receive notifications and approve actions via {operator.chat_provider}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Billing & Actions */}
          <div className="space-y-6">
            {/* Activation & Billing Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-6"
              data-testid="billing-card"
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Lightning size={24} className="text-pink-500" />
                  Activation & Billing
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  hostingActive 
                    ? 'bg-[#00E5C0]/20 text-[#00E5C0] border border-green-500/30' 
                    : 'bg-zinc-500/20 text-[#B0B8C4] border border-zinc-500/30'
                }`} data-testid="activation-status-badge">
                  {hostingActive ? 'Activated' : 'Not Activated'}
                </span>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                  <div className="flex items-center gap-2 text-[#A0AEC0] text-sm mb-1">
                    <HardDrives size={14} />
                    Monthly Cost
                  </div>
                  <p className="text-3xl font-bold text-[#14F195]" data-testid="monthly-cost">
                    Free<span className="text-lg text-[#A0AEC0] font-normal"> — credit-based compute</span>
                  </p>
                </div>

                {hostingActive ? (
                  <>
                    <div className="p-4 rounded-xl bg-[#00E5C0]/5 border border-green-500/20" data-testid="agent-activated-notice">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle size={18} className="text-[#00E5C0]" />
                        <span className="text-[#00E5C0] font-semibold">Operator Active</span>
                      </div>
                      <p className="text-[#A0AEC0] text-sm">
                        Your operator is ready to execute tasks.
                      </p>
                    </div>
                    
                    {/* Pause Agent Button */}
                    {canPause && (
                      <button
                        onClick={handlePause}
                        disabled={actionLoading === 'pause'}
                        className="w-full px-4 py-3 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                        data-testid="billing-pause-btn"
                      >
                        {actionLoading === 'pause' ? (
                          <CircleNotch size={18} className="animate-spin" />
                        ) : (
                          <>
                            <Pause size={18} />
                            Pause Agent
                          </>
                        )}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20" data-testid="activation-required-notice">
                      <div className="flex items-center gap-2 mb-2">
                        <WarningCircle size={18} className="text-amber-400" />
                        <span className="text-amber-400 font-semibold">Activation Required</span>
                      </div>
                      <p className="text-[#A0AEC0] text-sm">
                        Activate this operator to start running tasks.
                      </p>
                    </div>
                    
                    <button
                      onClick={handleEnableHosting}
                      disabled={checkoutLoading}
                      className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#00E5C0] to-red-500 hover:opacity-90 text-white font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
                      data-testid="activate-agent-btn"
                    >
                      {checkoutLoading ? (
                        <>
                          <CircleNotch size={18} className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Lightning size={18} />
                          Activate Agent
                        </>
                      )}
                    </button>
                  </>
                )}

                <p className="text-[#7D8896] text-xs text-center">
                  Need help? Contact support or <a href="#" className="text-[#00E5C0] hover:underline">view pricing details</a>
                </p>
              </div>
            </motion.div>

            {/* Requirements Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-6"
            >
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <WarningCircle size={20} className="text-amber-400" />
                Device Requirements
              </h3>
              <p className="text-[#A0AEC0] text-sm mb-3">
                For operators to execute commands, your connected device must:
              </p>
              <ul className="space-y-2 text-sm text-[#A0AEC0]">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>
                  Stay <strong className="text-amber-300">awake</strong> (disable sleep mode)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400"></div>
                  Stay <strong className="text-amber-300">online</strong> (maintain internet connection)
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5"></div>
                  <div>
                    Have the connector <strong className="text-amber-300">running</strong>
                    <p className="text-xs text-[#7D8896] mt-1">
                      The connector is a small app that runs on your computer and lets the operator execute tasks locally. 
                      Click &quot;PencilSimple&quot; above and go to Step 4 to install and connect it.
                    </p>
                  </div>
                </li>
              </ul>
              
              {!connector && (
                <button
                  onClick={() => setWizardOpen(true)}
                  className="mt-4 w-full px-4 py-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Terminal size={16} />
                  Setup Connector Now
                </button>
              )}
            </motion.div>

            {/* ArrowsClockwise Button */}
            <button
              onClick={fetchOperator}
              className="w-full px-4 py-3 rounded-xl glass hover:bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:text-white font-medium flex items-center justify-center gap-2 transition-colors"
              data-testid="refresh-status-btn"
            >
              <ArrowsClockwise size={18} />
              Refresh Status
            </button>
          </div>
        </div>
            </motion.div>
          )}

          {/* Tasks Tab - Agentic Task Queue */}
          {activeTab === 'tasks' && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <TasksTab operatorId={operatorId} operator={operator} />
            </motion.div>
          )}

          {/* Intelligence Tab - Governed Intelligence System */}
          {activeTab === 'intelligence' && (
            <motion.div
              key="intelligence"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <OperatorIntelligence operatorId={operatorId} />
            </motion.div>
          )}

          {/* Connector Tab - NEW SIMPLIFIED API KEY BASED */}
          {activeTab === 'connector' && (
            <motion.div
              key="connector"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Simple Introduction */}
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <WifiHigh size={24} className="text-pink-500" />
                  Connect Your Device
                </h2>
                
                <p className="text-[#A0AEC0] mb-4">
                  Connect your computer to this Operator in <strong className="text-white">3 easy steps</strong>:
                </p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center font-bold mb-2">1</div>
                    <h4 className="text-white font-medium mb-1">Generate API Key</h4>
                    <p className="text-[#A0AEC0] text-sm">Click the button below to create a secure connection key</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center font-bold mb-2">2</div>
                    <h4 className="text-white font-medium mb-1">Install Connector</h4>
                    <p className="text-[#A0AEC0] text-sm">DownloadSimple and install the Python connector script</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center font-bold mb-2">3</div>
                    <h4 className="text-white font-medium mb-1">Run Command</h4>
                    <p className="text-[#A0AEC0] text-sm">Run the connector with your API key - that&apos;s it!</p>
                  </div>
                </div>
              </div>

              {/* Generate API Key Section */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Lock size={20} className="text-[#00E5C0]" />
                  Step 1: Generate API Key
                </h3>
                
                {/* Newly Generated Key Display */}
                {newlyGeneratedKey ? (
                  <div className="mb-6">
                    <div className="p-4 rounded-xl bg-[#00E5C0]/10 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle size={20} className="text-[#00E5C0]" />
                        <span className="text-[#00E5C0] font-semibold">Your API Key (save it now!)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-3 bg-[#1F2937] rounded-lg font-mono text-sm text-[#00E5C0] break-all">
                          {newlyGeneratedKey}
                        </code>
                        <button
                          onClick={handleCopyKey}
                          className="p-3 rounded-lg bg-[#1E2836] hover:bg-[#1A2030] transition-colors"
                        >
                          {copiedKey ? (
                            <Check size={20} className="text-[#00E5C0]" />
                          ) : (
                            <Copy size={20} className="text-white" />
                          )}
                        </button>
                      </div>
                      <p className="text-amber-400 text-sm mt-2 flex items-center gap-1">
                        <WarningCircle size={14} />
                        This key will only be shown once. Copy and save it securely!
                      </p>
                    </div>
                    <button
                      onClick={() => setNewlyGeneratedKey(null)}
                      className="mt-3 text-sm text-[#B0B8C4] hover:text-white"
                    >
                      Done, generate another key
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 mb-6">
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder={`Connector for ${operator?.name}`}
                      className="flex-1 px-4 py-3 rounded-lg bg-[#1F2937] border border-[rgba(255,255,255,0.08)] text-white placeholder-zinc-500 focus:border-[#00E5C0] focus:outline-none"
                    />
                    <button
                      onClick={handleGenerateKey}
                      disabled={generatingKey}
                      className="px-6 py-3 rounded-lg bg-gradient-to-r from-[#00E5C0] to-rose-500 hover:opacity-90 text-white font-semibold flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
                    >
                      {generatingKey ? (
                        <CircleNotch size={18} className="animate-spin" />
                      ) : (
                        <Lightning size={18} />
                      )}
                      Generate Key
                    </button>
                  </div>
                )}
                
                {/* Real-time Connection Status */}
                {connectorStatus && connectorKeys.filter(k => k.active).length > 0 && (
                  <div className={`p-4 rounded-lg border mb-4 ${
                    connectorStatus.is_polling 
                      ? 'bg-[#00E5C0]/10 border-green-500/30' 
                      : connectorStatus.status === 'recent'
                        ? 'bg-[#F59E0B]/10 border-yellow-500/30'
                        : 'bg-[#FF6B6B]/10 border-red-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          connectorStatus.is_polling 
                            ? 'bg-green-400 animate-pulse' 
                            : connectorStatus.status === 'recent'
                              ? 'bg-yellow-400'
                              : 'bg-red-400'
                        }`} />
                        <div>
                          <p className={`font-medium ${
                            connectorStatus.is_polling ? 'text-[#00E5C0]' : connectorStatus.status === 'recent' ? 'text-[#F59E0B]' : 'text-[#FF6B6B]'
                          }`}>
                            {connectorStatus.is_polling ? '🟢 Connector Online' : connectorStatus.status === 'recent' ? '🟡 Connector Idle' : '🔴 Connector Offline'}
                          </p>
                          <p className="text-sm text-[#B0B8C4]">{connectorStatus.message}</p>
                          {connectorStatus.recommendation && (
                            <p className="text-sm text-[#7D8896] mt-1">💡 {connectorStatus.recommendation}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Show reconnect instructions when offline */}
                    {!connectorStatus.is_polling && connectorStatus.status !== 'recent' && connectorKeys.some(k => k.last_used_at) && (
                      <div className="mt-3 p-3 rounded-lg bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                        <p className="text-sm text-white font-medium mb-2">📋 How to Reconnect:</p>
                        <p className="text-xs text-[#B0B8C4] mb-2">If you see connection errors in your terminal, restart the connector:</p>
                        <div className="bg-[#1F2937] rounded-lg p-3 font-mono text-xs space-y-1">
                          <p className="text-[#7D8896]"># Check connector status</p>
                          <p className="text-[#3B82F6]">python connector_v3.py --status</p>
                          <p className="text-[#7D8896] mt-2"># Stop the connector (if running)</p>
                          <p className="text-[#F59E0B]">Ctrl+C</p>
                          <p className="text-[#7D8896] mt-2"># Restart the connector</p>
                          <p className="text-[#00E5C0]">python connector_v3.py --key YOUR_API_KEY</p>
                        </div>
                        <p className="text-xs text-[#7D8896] mt-2">
                          💡 Find your API key in the &quot;Active Keys&quot; section below (or generate a new one).
                        </p>
                      </div>
                    )}
                    {connectorStatus.pending_commands > 0 && (
                      <p className="text-sm text-[#F59E0B] mt-2">
                        ⚠️ {connectorStatus.pending_commands} command(s) waiting to be executed
                      </p>
                    )}
                    <button
                      onClick={fetchConnectorStatus}
                      className="text-xs text-[#7D8896] hover:text-[#F0F4F8] mt-2 underline"
                    >
                      Refresh status
                    </button>
                  </div>
                )}
                
                {/* Existing Keys */}
                {connectorKeys.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3">Active Keys</h4>
                    <div className="space-y-2">
                      {connectorKeys.filter(k => k.active).map(key => (
                        <div 
                          key={key.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              connectorStatus?.is_polling ? 'bg-green-400 animate-pulse' : 
                              connectorStatus?.status === 'recent' ? 'bg-yellow-400' :
                              key.last_used_at ? 'bg-zinc-400' : 'bg-zinc-600'
                            }`} />
                            <div>
                              <span className="text-white font-medium">{key.name}</span>
                              <span className="text-[#7D8896] font-mono text-sm ml-2">
                                {key.key_prefix}...
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-[#B0B8C4]">
                            {key.device_name && (
                              <span className="px-2 py-1 rounded bg-[#1E2836]">
                                {key.device_name}
                              </span>
                            )}
                            {connectorStatus?.last_poll && (
                              <span className="text-xs">
                                Last poll: {connectorStatus.seconds_since_poll < 60 
                                  ? `${connectorStatus.seconds_since_poll}s ago` 
                                  : connectorStatus.seconds_since_poll < 3600
                                    ? `${Math.floor(connectorStatus.seconds_since_poll/60)}m ago`
                                    : new Date(connectorStatus.last_poll).toLocaleString()}
                              </span>
                            )}
                            <button
                              onClick={() => handleRevokeKey(key.id, key.name)}
                              className="p-1.5 rounded hover:bg-[#FF6B6B]/10 text-[#FF6B6B] transition-colors"
                              title="Revoke key"
                            >
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Installation Instructions */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <DownloadSimple size={20} className="text-[#3B82F6]" />
                  Step 2: Install & Run
                </h3>
                
                {/* One-Click Installer */}
                <div className="p-4 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 mb-4">
                  <p className="text-white font-medium mb-2">🚀 One-Click Installer (Recommended)</p>
                  <p className="text-sm text-[#B0B8C4] mb-3">
                    Downloads the connector, installs all dependencies, and runs diagnostics automatically.
                  </p>
                  <div className="flex gap-2">
                    <a
                      href={`${BACKEND_URL}/api/connector/download/install.bat`}
                      download="install.bat"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                    >
                      <Monitor size={16} />
                      Windows Installer
                    </a>
                    <a
                      href={`${BACKEND_URL}/api/connector/download/install_unix.sh`}
                      download="install.sh"
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00E5C0] hover:bg-green-600 text-white text-sm font-medium transition-colors"
                    >
                      <Terminal size={16} />
                      Mac/Linux Installer
                    </a>
                  </div>
                </div>
                
                {/* Requirements */}
                <div className="p-3 rounded-lg bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)] mb-4">
                  <p className="text-[#A0AEC0] text-sm">
                    <strong className="text-white">Requirements:</strong> Python 3.8+ (the installer will handle everything else)
                  </p>
                </div>

                {/* Platform Tabs */}
                <div className="mb-4">
                  <div className="flex gap-2 border-b border-[rgba(255,255,255,0.08)] pb-2">
                    <button
                      onClick={() => setConnectorPlatform && setConnectorPlatform('windows')}
                      className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                        (!connectorPlatform || connectorPlatform === 'windows') 
                          ? 'bg-blue-500/20 text-[#3B82F6] border-b-2 border-blue-400' 
                          : 'text-[#B0B8C4] hover:text-white'
                      }`}
                    >
                      <Monitor size={16} className="inline mr-2" />
                      Windows
                    </button>
                    <button
                      onClick={() => setConnectorPlatform && setConnectorPlatform('mac')}
                      className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                        connectorPlatform === 'mac' 
                          ? 'bg-[#00E5C0]/20 text-[#00E5C0] border-b-2 border-green-400' 
                          : 'text-[#B0B8C4] hover:text-white'
                      }`}
                    >
                      <Terminal size={16} className="inline mr-2" />
                      Mac / Linux
                    </button>
                  </div>
                </div>

                {/* Windows Instructions */}
                {(!connectorPlatform || connectorPlatform === 'windows') && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-white font-medium mb-2">1. Install Python (if not installed):</p>
                      <p className="text-[#A0AEC0] text-sm mb-2">
                        DownloadSimple from <a href="https://python.org/downloads" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] hover:underline">python.org/downloads</a> or install via Microsoft Store
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-white font-medium mb-2">2. Install dependencies (PowerShell):</p>
                      <pre className="bg-[#1F2937] rounded-lg p-4 overflow-x-auto text-sm text-[#3B82F6] font-mono">
py -m pip install requests
                      </pre>
                    </div>

                    <div>
                      <p className="text-white font-medium mb-2">3. DownloadSimple the connector (PowerShell):</p>
                      <pre className="bg-[#1F2937] rounded-lg p-4 overflow-x-auto text-sm text-[#3B82F6] font-mono whitespace-pre-wrap break-all">
{`Invoke-WebRequest -Uri "${window.location.origin}/api/exec/connector/connector.py" -OutFile "connector_v3.py"`}
                      </pre>
                    </div>
                    
                    <div>
                      <p className="text-white font-medium mb-2">4. Run with your API key:</p>
                      <div className="relative">
                        <pre className="bg-[#1F2937] rounded-lg p-4 pr-32 overflow-x-auto text-sm text-[#3B82F6] font-mono">
py connector_v3.py --key YOUR_API_KEY
                        </pre>
                        {newlyGeneratedKey && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`py connector_v3.py --key ${newlyGeneratedKey}`);
                              setCopiedCommand(true);
                              toast.success('Command copied!');
                              setTimeout(() => setCopiedCommand(false), 2000);
                            }}
                            className="absolute right-3 top-3 px-3 py-1.5 rounded-lg bg-[#00E5C0] hover:bg-pink-600 text-white text-xs font-medium flex items-center gap-1"
                          >
                            {copiedCommand ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy with key</>}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Mac/Linux Instructions */}
                {connectorPlatform === 'mac' && (
                  <div className="space-y-4">
                    <div>
                      <p className="text-white font-medium mb-2">1. Install dependencies (Terminal):</p>
                      <pre className="bg-[#1F2937] rounded-lg p-4 overflow-x-auto text-sm text-[#00E5C0] font-mono">
pip3 install requests
                      </pre>
                      <p className="text-[#7D8896] text-xs mt-1">Or use: <code className="text-[#00E5C0]">python3 -m pip install requests</code></p>
                    </div>

                    <div>
                      <p className="text-white font-medium mb-2">2. DownloadSimple the connector:</p>
                      <pre className="bg-[#1F2937] rounded-lg p-4 overflow-x-auto text-sm text-[#00E5C0] font-mono">
{`curl -sL ${window.location.origin}/api/exec/connector/connector.py -o connector_v3.py`}
                      </pre>
                    </div>
                    
                    <div>
                      <p className="text-white font-medium mb-2">3. Run with your API key:</p>
                      <div className="relative">
                        <pre className="bg-[#1F2937] rounded-lg p-4 pr-32 overflow-x-auto text-sm text-[#00E5C0] font-mono">
python3 connector_v3.py --key YOUR_API_KEY
                        </pre>
                        {newlyGeneratedKey && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`python3 connector_v3.py --key ${newlyGeneratedKey}`);
                              setCopiedCommand(true);
                              toast.success('Command copied!');
                              setTimeout(() => setCopiedCommand(false), 2000);
                            }}
                            className="absolute right-3 top-3 px-3 py-1.5 rounded-lg bg-[#00E5C0] hover:bg-pink-600 text-white text-xs font-medium flex items-center gap-1"
                          >
                            {copiedCommand ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy with key</>}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Success indicator */}
                <div className="mt-6 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                  <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                    <CheckCircle size={18} className="text-[#00E5C0]" />
                    You&apos;ll know it&apos;s working when you see:
                  </h4>
                  <pre className="bg-[#1F2937] rounded-lg p-3 text-xs text-[#00E5C0] font-mono">
{`============================================================
Man & Machine Connector v${latestConnectorVersion}
Platform: ${(!connectorPlatform || connectorPlatform === 'windows') ? 'Windows 10' : 'Darwin (macOS)'}
============================================================
Connecting to server...
✓ Connected! Agent: ${operator?.name || 'Your Operator'}
✓ Configuration loaded
Ready to receive commands...`}
                  </pre>
                </div>
              </div>

              {/* Troubleshooting */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <WarningCircle size={20} className="text-amber-400" />
                  Troubleshooting
                </h3>
                <ul className="space-y-2 text-sm text-[#A0AEC0]">
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E5C0]">•</span>
                    <span><strong className="text-white">Connection refused?</strong> Check your firewall allows outbound HTTPS (port 443)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E5C0]">•</span>
                    <span><strong className="text-white">Invalid API key?</strong> Generate a new key above and try again</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00E5C0]">•</span>
                    <span><strong className="text-white">Python not found?</strong> Install from <a href="https://python.org/downloads" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] hover:underline">python.org/downloads</a></span>
                  </li>
                </ul>
              </div>
            </motion.div>
          )}

          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <motion.div
              key="chat"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Working Banner - Shows when agent is processing */}
              <WorkingBanner 
                operatorId={operatorId} 
                onViewTasks={() => setActiveTab('tasks')}
              />
              
              {/* Chat Integrations Header */}
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                  <ChatText size={24} className="text-pink-500" />
                  Chat Integrations
                </h2>
                <p className="text-[#A0AEC0]">
                  Connect Telegram or WhatsApp to chat with your operator, receive notifications, and approve/deny actions on the go.
                </p>
              </div>

              {/* Current Status */}
              {(operator.chat_provider && operator.chat_provider !== 'none') || operator.has_telegram_token || operator.has_whatsapp_token ? (
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Active Integration</h3>
                  <div className="p-4 rounded-xl bg-[#00E5C0]/10 border border-green-500/20">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        operator.has_telegram_token ? 'bg-blue-500/20' : 'bg-[#00E5C0]/20'
                      }`}>
                        {operator.has_telegram_token ? (
                          <PaperPlaneRight size={24} className="text-[#3B82F6]" />
                        ) : (
                          <ChatText size={24} className="text-[#00E5C0]" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold">
                          {operator.has_telegram_token ? 'Telegram' : 'WhatsApp'}
                        </h4>
                        <p className="text-[#00E5C0] text-sm flex items-center gap-1">
                          <CheckCircle size={14} />
                          Integration active
                          {telegramBotUsername && operator.has_telegram_token && (
                            <span className="ml-2 text-[#3B82F6]">@{telegramBotUsername}</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => setWizardOpen(true)}
                        className="px-4 py-2 rounded-lg bg-[#1A2030] hover:bg-zinc-600 text-white text-sm transition-colors"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                  
                  {/* How to Create a New Bot - Collapsible */}
                  <details className="mt-4">
                    <summary className="text-[#B0B8C4] text-sm cursor-pointer hover:text-white transition-colors flex items-center gap-2">
                      <span>📘 Need to create a new bot?</span>
                    </summary>
                    <div className="mt-3 grid md:grid-cols-2 gap-4">
                      {/* Telegram Instructions */}
                      <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
                        <h5 className="font-medium text-blue-300 text-sm mb-2 flex items-center gap-2">
                          <PaperPlaneRight size={14} />
                          Create a Telegram Bot
                        </h5>
                        <ol className="text-xs text-[#B0B8C4] space-y-1.5">
                          <li>1. Open Telegram, search <span className="text-blue-300 font-medium">@BotFather</span></li>
                          <li>2. PaperPlaneRight <code className="bg-[#1A2030] px-1 rounded">/newbot</code></li>
                          <li>3. Choose a name and username for your bot</li>
                          <li>4. Copy the API token provided</li>
                          <li>5. Click <strong className="text-white">Change</strong> above to enter the token</li>
                        </ol>
                        <a 
                          href="https://t.me/botfather" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs text-[#3B82F6] hover:text-blue-300"
                        >
                          <ArrowSquareOut size={10} />
                          Open @BotFather
                        </a>
                      </div>
                      
                      {/* WhatsApp Instructions */}
                      <div className="p-4 rounded-xl bg-[#00E5C0]/5 border border-green-500/20">
                        <h5 className="font-medium text-[#00E5C0] text-sm mb-2 flex items-center gap-2">
                          <ChatText size={14} />
                          Create a WhatsApp Business
                        </h5>
                        <ol className="text-xs text-[#B0B8C4] space-y-1.5">
                          <li>1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] underline">Meta for Developers</a></li>
                          <li>2. Create a new app → select <span className="text-[#00E5C0]">WhatsApp</span></li>
                          <li>3. Go to <span className="text-white">WhatsApp → API Setup</span></li>
                          <li>4. Copy Phone Number ID, Business ID, Access Token</li>
                          <li>5. Click <strong className="text-white">Change</strong> above to configure</li>
                        </ol>
                        <a 
                          href="https://developers.facebook.com/apps" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-xs text-[#00E5C0] hover:text-[#00E5C0]"
                        >
                          <ArrowSquareOut size={10} />
                          Meta for Developers
                        </a>
                      </div>
                    </div>
                  </details>
                  
                  {/* Telegram Pairing Section */}
                  {operator.has_telegram_token && (
                    <div className="mt-6 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
                      <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                        <Link size={16} className="text-[#3B82F6]" />
                        Connect Your Telegram Chat
                      </h4>
                      <p className="text-[#B0B8C4] text-sm mb-4">
                        Connect your Telegram to this operator to send commands and receive notifications.
                      </p>
                      
                      {/* Primary Method: Manual Command */}
                      <div className="p-4 rounded-lg bg-[#1E2836]/50 border border-[rgba(255,255,255,0.08)]">
                        <p className="text-white text-sm font-medium mb-3">📱 To connect, send this command in Telegram:</p>
                        
                        <div className="flex items-center gap-2 mb-3">
                          <code className="flex-1 px-3 py-2 rounded bg-[#1F2937] text-[#00E5C0] font-mono text-sm">
                            /start {operator.id}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(`/start ${operator.id}`);
                              toast.success('Command copied to clipboard!');
                            }}
                            className="px-3 py-2 rounded bg-[#1A2030] hover:bg-zinc-600 text-white transition-colors"
                            title="Copy command"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                        
                        <ol className="text-[#B0B8C4] text-sm space-y-2">
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">1.</span>
                            <span>Open Telegram and search for your bot: <span className="text-[#3B82F6] font-medium">{telegramBotUsername ? `@${telegramBotUsername}` : '(your bot name)'}</span></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">2.</span>
                            <span>Send the command above (or click copy and paste it)</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[#00E5C0] font-bold">3.</span>
                            <span>The bot will confirm: &quot;✅ Successfully connected!&quot;</span>
                          </li>
                        </ol>
                      </div>
                      
                      {/* Alternative: Deep Link (may not work in all browsers) */}
                      {telegramBotUsername && (
                        <details className="mt-4">
                          <summary className="text-[#7D8896] text-xs cursor-pointer hover:text-white transition-colors">
                            Alternative: Quick link (may not work in all browsers)
                          </summary>
                          <div className="mt-2">
                            <a
                              href={`https://t.me/${telegramBotUsername}?start=${operator.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex px-4 py-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-[#3B82F6] text-sm font-medium items-center gap-2 transition-colors"
                              data-testid="telegram-connect-btn"
                            >
                              <PaperPlaneRight size={16} />
                              Open in Telegram
                              <ArrowSquareOut size={12} />
                            </a>
                            <p className="text-xs text-[#5C6778] mt-2">
                              Note: This opens a link that may show an error in your browser console. 
                              If Telegram Desktop is installed, it should open automatically.
                            </p>
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Choose Integration</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Telegram Card */}
                    <div className="p-6 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] hover:border-blue-500/30 transition-colors">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                          <PaperPlaneRight size={24} className="text-[#3B82F6]" />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold">Telegram</h4>
                          <p className="text-[#7D8896] text-sm">Bot-based integration</p>
                        </div>
                      </div>
                      <p className="text-[#A0AEC0] text-sm mb-4">
                        Connect via Telegram bot. Get notifications, chat with your operator, and approve actions directly in Telegram.
                      </p>
                      
                      {/* How to Create Telegram Bot */}
                      <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 mb-4">
                        <p className="text-blue-300 text-xs font-medium mb-2">How to create a Telegram bot:</p>
                        <ol className="text-xs text-[#B0B8C4] space-y-1">
                          <li>1. Open Telegram, search <span className="text-blue-300">@BotFather</span></li>
                          <li>2. PaperPlaneRight <code className="bg-[#1A2030] px-1 rounded">/newbot</code></li>
                          <li>3. Follow prompts to name your bot</li>
                          <li>4. Copy the API token provided</li>
                        </ol>
                      </div>
                      
                      <button
                        onClick={() => setWizardOpen(true)}
                        className="w-full px-4 py-2.5 rounded-lg bg-blue-500/10 text-[#3B82F6] hover:bg-blue-500/20 transition-colors font-medium"
                      >
                        Setup Telegram
                      </button>
                    </div>

                    {/* WhatsApp Card */}
                    <div className="p-6 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)] hover:border-green-500/30 transition-colors">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-[#00E5C0]/10 flex items-center justify-center">
                          <ChatText size={24} className="text-[#00E5C0]" />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold">WhatsApp</h4>
                          <p className="text-[#7D8896] text-sm">Business API integration</p>
                        </div>
                      </div>
                      <p className="text-[#A0AEC0] text-sm mb-4">
                        Connect via WhatsApp Business. Chat with your operator and manage approvals from your phone.
                      </p>
                      
                      {/* How to Setup WhatsApp */}
                      <div className="p-3 rounded-lg bg-[#00E5C0]/5 border border-green-500/20 mb-4">
                        <p className="text-[#00E5C0] text-xs font-medium mb-2">How to set up WhatsApp Business API:</p>
                        <ol className="text-xs text-[#B0B8C4] space-y-1">
                          <li>1. Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#00E5C0] underline">Meta for Developers</a></li>
                          <li>2. Create a WhatsApp Business App</li>
                          <li>3. Get credentials from WhatsApp &gt; API Setup</li>
                        </ol>
                      </div>
                      
                      <button
                        onClick={() => setWizardOpen(true)}
                        className="w-full px-4 py-2.5 rounded-lg bg-[#00E5C0]/10 text-[#00E5C0] hover:bg-[#00E5C0]/20 transition-colors font-medium"
                      >
                        Setup WhatsApp
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* How It Works */}
              <div className="glass rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">How Chat Integration Works</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center text-sm font-bold mb-3">1</div>
                    <h4 className="text-white font-medium mb-1">Connect</h4>
                    <p className="text-[#7D8896] text-sm">Choose your preferred chat platform and follow the setup wizard</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center text-sm font-bold mb-3">2</div>
                    <h4 className="text-white font-medium mb-1">Chat</h4>
                    <p className="text-[#7D8896] text-sm">Send messages to your operator and receive responses in real-time</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)]">
                    <div className="w-8 h-8 rounded-full bg-[#00E5C0]/20 text-[#00E5C0] flex items-center justify-center text-sm font-bold mb-3">3</div>
                    <h4 className="text-white font-medium mb-1">Approve</h4>
                    <p className="text-[#7D8896] text-sm">When actions require approval, you&apos;ll get a notification to approve or deny</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* GearSix Tab */}
          {activeTab === 'settings' && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Persona GearSix - Orion Feature */}
              <PersonaSettings 
                agentId={operatorId}
                currentName={personaName}
                onNameChange={(newName) => {
                  setPersonaName(newName);
                  // Update operator state to reflect new persona name
                  if (operator) {
                    setOperator({...operator, persona_name: newName});
                  }
                }}
              />
              
              {/* Connector Update Section */}
              <ConnectorUpdate 
                agentId={operatorId}
                connectorKeys={connectorKeys}
                connectorStatus={connectorStatus}
              />
              
              {/* Safety GearSix */}
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Shield size={24} className="text-pink-500" />
                  Safety GearSix
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <Shield size={20} className="text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Require Approval for Destructive Actions</h3>
                        <p className="text-[#7D8896] text-sm">File deletions, system changes, etc. need your confirmation</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      operator.require_approval_for_destructive
                        ? 'bg-[#00E5C0]/20 text-[#00E5C0]'
                        : 'bg-zinc-500/20 text-[#B0B8C4]'
                    }`}>
                      {operator.require_approval_for_destructive ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#00E5C0]/10 flex items-center justify-center">
                        <CheckCircle size={20} className="text-[#00E5C0]" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">Auto-approve Read-Only Actions</h3>
                        <p className="text-[#7D8896] text-sm">File reads, directory listings, etc. proceed automatically</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      operator.auto_approve_read_only
                        ? 'bg-[#00E5C0]/20 text-[#00E5C0]'
                        : 'bg-zinc-500/20 text-[#B0B8C4]'
                    }`}>
                      {operator.auto_approve_read_only ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                <p className="mt-4 text-[#7D8896] text-sm">
                  To change safety settings, click the <strong className="text-white">Edit</strong> button above and go to Step 3.
                </p>
              </div>

              {/* Workspace GearSix - File Management */}
              <WorkspaceSettings agentId={operatorId} />

              {/* Operator Activation */}
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard size={24} className="text-pink-500" />
                  Operator Activation
                </h2>
                
                <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                  <div>
                    <p className="text-white font-semibold">Activation Status</p>
                    <p className="text-[#7D8896] text-sm">Free hosting — compute via credits</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    hostingActive 
                      ? 'bg-[#00E5C0]/20 text-[#00E5C0] border border-green-500/30' 
                      : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                  }`}>
                    {hostingActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {!hostingActive && (
                  <button
                    onClick={handleEnableHosting}
                    disabled={checkoutLoading}
                    className="mt-4 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-[#00E5C0] to-red-500 hover:opacity-90 text-white font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
                  >
                    {checkoutLoading ? (
                      <>
                        <CircleNotch size={18} className="animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Activate Operator — Free
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* LLM Configuration */}
              <div className="glass rounded-2xl p-6">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Sparkle size={24} className="text-pink-500" />
                  LLM Configuration
                </h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.05)]">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        operator.use_org_llm_key ? 'bg-[#00E5C0]/10' : 'bg-[#00E5C0]/10'
                      }`}>
                        {operator.use_org_llm_key ? (
                          <Shield size={20} className="text-[#00E5C0]" />
                        ) : (
                          <Lightning size={20} className="text-[#00E5C0]" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold">
                          {operator.use_org_llm_key ? 'Using Organization LLM Key' : 'Using Custom LLM Key'}
                        </h3>
                        <p className="text-[#7D8896] text-sm">
                          {operator.use_org_llm_key 
                            ? 'This operator uses the LLM API key from your organization settings'
                            : `Provider: ${operator.llm_provider || 'Not set'} • Model: ${operator.llm_model || 'Not set'}`
                          }
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      operator.use_org_llm_key
                        ? 'bg-[#00E5C0]/20 text-[#00E5C0]'
                        : 'bg-[#00E5C0]/20 text-[#00E5C0]'
                    }`}>
                      {operator.use_org_llm_key ? 'Organization' : 'Custom'}
                    </span>
                  </div>
                  
                  {!operator.use_org_llm_key && (
                    <div className="p-4 rounded-xl bg-[#00E5C0]/5 border border-purple-500/20">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-[#7D8896]">Provider</span>
                          <p className="text-white font-medium capitalize">{operator.llm_provider || 'Not configured'}</p>
                        </div>
                        <div>
                          <span className="text-[#7D8896]">Model</span>
                          <p className="text-white font-medium">{operator.llm_model || 'Not configured'}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-[#7D8896]">API Key</span>
                          <p className="text-white font-medium flex items-center gap-2">
                            {operator.has_llm_api_key ? (
                              <>
                                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                                Configured
                              </>
                            ) : (
                              <>
                                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                                Not set
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <p className="mt-4 text-[#7D8896] text-sm">
                  To change LLM settings, click the <strong className="text-white">Edit</strong> button above and go to Step 6.
                </p>
              </div>

              {/* Danger Zone */}
              <div className="glass rounded-2xl p-6 border border-red-500/20">
                <h2 className="text-xl font-bold text-[#FF6B6B] mb-4 flex items-center gap-2">
                  <ShieldSlash size={20} />
                  Danger Zone
                </h2>
                
                <p className="text-[#A0AEC0] mb-4 text-sm">
                  Deleting this operator will permanently remove it and revoke all connector access. 
                  The connector on your device will be disconnected and all data removed.
                </p>

                <button
                  onClick={handleDeleteAndDisconnect}
                  disabled={actionLoading === 'delete'}
                  className="px-4 py-2 rounded-lg border border-red-500/30 bg-[#FF6B6B]/10 hover:bg-[#FF6B6B]/20 text-[#FF6B6B] font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                >
                  {actionLoading === 'delete' ? (
                    <CircleNotch size={18} className="animate-spin" />
                  ) : (
                    <Trash size={18} />
                  )}
                  Delete Operator & Disconnect All Devices
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Operator PencilSimple Wizard Modal */}
      <CreateOperatorModal
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => {
          setWizardOpen(false);
          fetchOperator(); // ArrowsClockwise operator data
        }}
        orgId={activeOrganization?.organization_id}
        operator={operator}
      />
      
      {/* Chat Modal */}
      <OperatorChatModal
        isOpen={showChatModal}
        onClose={() => setShowChatModal(false)}
        operator={operator}
      />
    </>
  );
};

export default OperatorDetail;
